import React, {useMemo, useState} from "react";
import styles from "./styles";
import {Cell, Column, HeaderProps, Renderer, Row, useExpanded, useTable} from "react-table";
import {
    MdOutlineKeyboardArrowDown,
    MdOutlineKeyboardArrowRight,
} from "react-icons/md";
import {Box, Flex, Img, Text, Tooltip} from "@chakra-ui/react";
import {useSelector} from "react-redux";
import {
    Attribute,
    kpiStateSelector,
    TableMetaDataRows,
} from "../../store/kpis";
import {useCurrencySign} from "../../utils/custom-hooks/useCurrencySign";
import {ShowBy} from "../../features/wideviews-financial/WideViewsFinancialNew";
import {GroupBy} from "../../features/workspace/Workspace";
import {productsFieldsSelector} from "../../store/products";
import {pnlDataLanguageSelector} from "../../store/pnl";
import InformationWrapper from "../InformationWrapper/InformationWrapper";
import PlatformChip from "../platform-chip/platformChip";
import {channelsStateSelector} from "../../store/channels";

interface Props {
    data?: any[];
    allExpendedRows: { [key: string]: boolean };
    setAllExpendedRows: any;
    tableRef: React.RefObject<any>;
    showBy: any;
    groupBy: any;
}

const PnlTable = (props: Props) => {
    const [selectedColumnToBold, setSelectedColumnToBold] = useState<string>('');
    const pnlElements = useSelector(pnlDataLanguageSelector)?.data;
    const channels = useSelector(channelsStateSelector)?.channels;
    const pnlTableConfig = useSelector(kpiStateSelector)?.pnlTableConfig?.data;
    const currentCurrency = useCurrencySign();
    const productsNames = useSelector(productsFieldsSelector);
    const getHeaders = () => {
        if (!!props?.data && props?.data?.length > 0) {
            return Object.keys(props?.data[0]);
        } else {
            return [];
        }
    }

    const headers = getHeaders()


    const renderExpandIcon = (isRowExpanded: boolean): JSX.Element => {
        if (isRowExpanded) {
            return <MdOutlineKeyboardArrowDown fontSize={20}/>
        } else {
            return <MdOutlineKeyboardArrowRight fontSize={20}/>
        }
    }

    const renderExpandIconPlaceholder = (): JSX.Element => {
        return <MdOutlineKeyboardArrowRight opacity={0} fontSize={20}/>
    }

    const onRowExpandClick = (row: Row) => {
        if (row?.canExpand) {
            if (row?.id in props?.allExpendedRows) {
                props?.setAllExpendedRows((prev: any) => {
                    return {...prev, [row.id]: !prev[row?.id]};
                });
            } else {
                props?.setAllExpendedRows((prev: any) => {
                    return {...prev, [row.id]: true};
                });
            }
        }
    };


    const renderExpandColumn = (header: string) => {
        let displayHeader = header;

        return {
            id: "expander",
            Header: displayHeader,
            accessor: header,
            Cell: ({
                       row,
                       cell,
                       getToggleAllRowsExpandedProps,
                       isAllRowsExpanded,
                   }: {
                row: any;
                cell: Cell;
                getToggleAllRowsExpandedProps: any;
                isAllRowsExpanded: boolean;
            }) => {
                return (
                    <Flex
                        marginLeft={row?.depth > 0 ? row?.depth * 20 + "px" : 0}
                        alignItems={"center"}
                        {...row.getToggleRowExpandedProps({title: undefined})}
                    >
              <span>
                {row?.canExpand
                    ? renderExpandIcon(row?.isExpanded)
                    : renderExpandIconPlaceholder()}
              </span>
                        <InformationWrapper text={cell?.value}/>
                    </Flex>
                );
            },
        };
    };

    function reorderHeaders(
        headers: {
            Header: string;
            accessor: string;
            id?: string;
        }[]
    ): {
        Header: string;
        accessor: string;
        id?: string;
    }[] {
        const expanderIndex = headers.findIndex(
            (header) => header?.id === "expander"
        );
        if (expanderIndex !== -1) {
            const expanderHeader = headers.splice(expanderIndex, 1)[0];
            headers.unshift(expanderHeader);
        }
        return headers;
    }

    const createColumns = (): Column<any>[] => {
        // @ts-ignore
        const headersToReturn = headers.map((header: string) => {
            let displayHeader = header
            if (header === 'subRows') {
                return
            }
            // expander row
            if (header === 'name') {
                return renderExpandColumn(header)
            }

            if (props?.showBy === ShowBy.CHANNEL) {
                displayHeader = header.replace(/_/g, '.')
            }
            return {Header: displayHeader, accessor: header}
        }).filter((header) => !!header)


        // @ts-ignore
        return reorderHeaders(headersToReturn)
    }

    const sortColumnsByGrossRevenue = (rowData: { [key: string]: string }): { [key: string]: string } => {
        const {name, total, ...rest} = rowData;
        const sortedColumns = Object.entries(rest).sort(([, value1], [, value2]) => {
            // sort in descending order
            return Number(value2) - Number(value1);
        });
        const reorderedObject = {
            name,
            ...Object.fromEntries(sortedColumns),
        };
        return reorderedObject;
    }

    const normalizeData = (data?: any[]) => {
        return data?.map((row: any) => {
            const newRow: any = {};

            for (let key in row) {
                if (row.hasOwnProperty(key)) {
                    let value = row[key];
                    if (typeof value === 'number') {
                        value = value?.toLocaleString('en-us', {maximumFractionDigits: 2})
                    }
                    if (Array.isArray(value)) {
                        value = normalizeData(value)
                    }

                    newRow[key] = value;
                }
            }

            if (props?.showBy === ShowBy.PRODUCT && newRow?.hasOwnProperty('name') && newRow['name'] === 'Gross Revenue') {
                // return sortColumnsByGrossRevenue(newRow)
            }


            return newRow;
        });
    };

    const columns = useMemo(() => createColumns(), [props?.data, productsNames]);
    const data = useMemo(() => normalizeData(props?.data), [props?.data, productsNames]);


    const handleClickOnCell = (cell: Cell) => {
        setSelectedColumnToBold(cell?.column?.id);
    }

    const renderTableGroupByProductsHeader = (header?: Renderer<HeaderProps<object>> | undefined, opacity?: number) => {

        const renderToolTip = (imageUrl?: string, variantTitle?: string, sku?: string) => {
            const fallbackImage = `data:image/png;base64,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`;

            return (
                <Flex opacity={opacity} fontWeight={'light'} maxW={'110px'} gap={'3px'} flexDir={'column'}>
                    <Flex gap={'3px'}>
                        <Text maxW={'100%'} overflowWrap={'break-word'}>{sku}</Text>
                    </Flex>
                    <Flex gap={'3px'}>
                        <Text maxW={'100px'} overflowWrap={'break-word'}>{variantTitle}</Text>
                    </Flex>
                    <Img onError={(event) => {
                        const imgElement = event.target as HTMLImageElement;
                        imgElement.src = fallbackImage;
                    }} maxW={'100px'} maxH={'100px'} src={imageUrl ?? fallbackImage}/>
                </Flex>
            )
        }

        if (props?.showBy === ShowBy.PRODUCT) {
            if (!header) {
                return ''
            }
            if (header === 'Total') {
                return header
            } else {
                const foundProduct = productsNames?.find((product) => product?.id === header)

                const rowChannel = foundProduct?.wvChannelId;
                const foundChannel = channels?.find((channel) => channel?.id === rowChannel);
                const foundChannelDisplayName = foundChannel?.displayName;
                const foundChannelPlatform = foundChannel?.platformType;

                return (
                    <Tooltip height={'100%'} placement={'top'} hasArrow bg={'white'} color={'gray'}
                             label={renderToolTip(foundProduct?.imageUrl,
                                 foundProduct?.variantTitle,
                                 foundProduct?.sku)}>
                        <Box height={'100%'}>
                            <PlatformChip text={foundChannelDisplayName}
                                          platform={foundChannelPlatform}/>

                            {foundProduct?.productTitle ?? foundProduct?.sku}
                        </Box>
                    </Tooltip>
                );
            }
        } else {
            return header
        }

    }

    const RenderTable = ({data}: { data: any[] }) => {

        let tableRowsConfig: TableMetaDataRows[] | undefined = [];
        if (!!pnlTableConfig && pnlTableConfig?.length > 0) {
            tableRowsConfig = pnlTableConfig[0]?.rows;
        }

        const allRowsConfig = tableRowsConfig?.find((item) => item.fieldName === 'AllRows')?.attribute;
        const boldTotal = allRowsConfig?.find((item) => item.key === 'boldTotal')?.value === 'true'

        const {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            rows,
            prepareRow,
        } = useTable({
                columns,
                data,
                initialState: {expanded: props?.allExpendedRows},
                // cell: (value: any, row: any) => {
                //     return {value, row}
                // }
            },
            useExpanded,
        )

        return (
            <styles.StyledTable ref={props.tableRef} {...getTableProps()} id='table'>
                <thead>
                {headerGroups.map((headerGroup, i) => (
                    <styles.StyledTr rowIndex={1} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => {
                            if (column.Header === 'name') {
                                return <styles.StyledThName  {...column.getHeaderProps()} />
                            }
                            // @ts-ignore
                            const maxWidthOfColumn = column?.Header?.length * 7 <= 250 ? column?.Header?.length * 7 : 250;
                            return (<styles.StyledTh minWidth={props?.showBy === ShowBy.PRODUCT ? maxWidthOfColumn :
                                props?.showBy === ShowBy.DATE && props?.groupBy === GroupBy.WEEK ? 100 : 0}
                                                     onClick={() => {
                                                         setSelectedColumnToBold(column?.id)
                                                     }}
                                                     highlightCell={column?.id === selectedColumnToBold}  {...column.getHeaderProps()}>

                                {renderTableGroupByProductsHeader(column?.Header, 1)}

                            </styles.StyledTh>);
                        })}
                    </styles.StyledTr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {rows.map((row: Row<{ name?: string }>, i) => {
                    let rowAttributes: Attribute[] | undefined = [];
                    const rowName = row?.original?.name;
                    const foundConfigForRow = tableRowsConfig?.find((item) => item?.fieldName === rowName);
                    if (!!foundConfigForRow) {
                        rowAttributes = foundConfigForRow?.attribute;
                    }
                    if (row?.depth > 0) { // meaning a sub row
                        const parentRowId = row?.id[0];
                        const foundParentRow: Row<{ name?: string }> | undefined = rows?.find((row: Row<{
                            name?: string
                        }>) => parentRowId === row?.id);

                        // Inherit parent attributes: Some attributes of the parent row also apply to its children. This is necessary because the name of the children rows may be unknown, such as the attribute 'displayCurrencyForAllSubRows'.
                        const foundConfigForParentRow: Attribute[] | undefined = tableRowsConfig?.find((item) => item?.fieldName === foundParentRow?.original?.name)?.attribute;

                        const isDisplayCurrencyForAllSubRowsOfParent = foundConfigForParentRow?.find((attribute) => attribute?.key === 'displayCurrencyForAllSubRows')?.value === 'true';
                        if (isDisplayCurrencyForAllSubRowsOfParent) {
                            rowAttributes?.push({key: 'displayCurrency', value: "true"})
                        }
                    }
                    const isDisplayTitleBold = row?.depth === 0 ? true : rowAttributes?.find((attribute) => attribute?.key === 'boldTitle')?.value === 'true';
                    const isDisplayCurrency = row?.depth === 0 ? true : rowAttributes?.find((attribute) => attribute?.key === 'displayCurrency')?.value === 'true';
                    const rowBackgroundColor = rowAttributes?.find((attribute) => attribute?.key === 'rowBackgroundColor')?.value ?? 'white';
                    const percentageFrom = rowAttributes?.find((attribute) => attribute?.key === 'percentageFrom')?.value;
                    const percentageFromTitle = rowAttributes?.find((attribute) => attribute?.key === 'percentageFromTitle')?.value;
                    const percentageFromBolder = rowAttributes?.find((attribute) => attribute?.key === 'percentageFromBolder')?.value === 'true';
                    const rowTooltip = rowAttributes?.find((attribute) => attribute?.key === 'tooltip')?.value;

                    prepareRow(row);
                    return (
                        <styles.StyledTr rowIndex={i} {...row.getRowProps()}>
                            {row.cells.map((cell) => {
                                let isDisplayBoldText = false;
                                if (boldTotal && cell?.column?.Header === 'Total') {
                                    isDisplayBoldText = true
                                }
                                if (cell.column.Header === "name") {
                                    return (
                                        <styles.StyledTdName onClick={() => {
                                            onRowExpandClick(row)
                                        }} backgroundColor={rowBackgroundColor}
                                                             displayBoldTitle={isDisplayTitleBold} {...cell.getCellProps()}>
                                            <Flex>
                                                {cell.render("Cell")}
                                                &nbsp;
                                                {!!percentageFromTitle ? `${percentageFromTitle}` : ''}
                                            </Flex>

                                        </styles.StyledTdName>
                                    );
                                }
                                let cellValue = cell?.value;
                                const foundPnlElementForRow = pnlElements?.find((element) => element?.displayName === row?.original?.name);
                                if (!!foundPnlElementForRow && !foundPnlElementForRow?.isDefault) {
                                    const parsedCellValue = parseFloat(!!cellValue ? cellValue.replace(/,/g, '').replace('.', '.') : '0');
                                    cellValue = parsedCellValue * -1
                                }

                                let percentageFromValue = '';
                                if (!!percentageFrom) {
                                    // @ts-ignore
                                    let columnAccessor: string = cell?.column?.Header;
                                    const foundRow = rows?.find((row: Row<{
                                        name?: string
                                    }>) => row?.original?.name === percentageFrom);
                                    if (props?.showBy === ShowBy.CHANNEL) {
                                        columnAccessor = columnAccessor.replace(/\./g, '_');
                                    }

                                    if (!!foundRow && !!columnAccessor && columnAccessor in foundRow?.original) {
                                        const columnFromValue = foundRow?.original[columnAccessor];

                                        const parsedColumnFromValue = parseFloat(columnFromValue.replace(/,/g, '').replace('.', '.'));
                                        const parsedCellValue = parseFloat(!!cellValue ? cellValue.replace(/,/g, '').replace('.', '.') : '0');
                                        const valueOfPercentage = !!parsedColumnFromValue ? (parsedCellValue / parsedColumnFromValue) * 100 : 0
                                        percentageFromValue = ` (${valueOfPercentage?.toLocaleString('en-US', {maximumFractionDigits: 0})}%)`
                                    }
                                }


                                return (
                                    <styles.StyledTd boldText={isDisplayBoldText} backgroundColor={rowBackgroundColor}
                                                     highlightCell={cell?.column?.id === selectedColumnToBold}
                                                     onClick={() => {
                                                         handleClickOnCell(cell)
                                                     }} {...cell.getCellProps()}>
                                        <Flex>
                                            {!!isDisplayCurrency && currentCurrency}
                                            {cellValue}
                                            &nbsp;
                                            {<Box
                                                fontWeight={percentageFromBolder ? 500 : 0}>{percentageFromValue}</Box>}
                                        </Flex>

                                    </styles.StyledTd>
                                );
                            })}
                        </styles.StyledTr>
                    );
                })}
                </tbody>
            </styles.StyledTable>
        );
    }


    return (
        // @ts-ignore
        <styles.TableHolder>
            {!!data && <RenderTable data={data}/>}
        </styles.TableHolder>
    );
};

export default PnlTable;
